import React, { useCallback, useState } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import { IconDownload, IconEye, IconX } from '@tabler/icons';
import { isEmpty } from 'lodash';
import { gridSpacing } from '../../common/themes/constants';
import { MyButton, MyIconButton, MyTypography } from '../common/components';
import StringUtils from '../common/services/StringUtils';
import FileUtils from '../common/services/FileUtils';
import DescriptionIcon from '@mui/icons-material/Description';
import GlobalLoader from '../common/components/loader/GlobalLoader';

export default function UploadedDocuments({
  label = 'Referral Documents',
  patientDetails = {},
  referralDetails = {},
  data_referral_answers,
  uploadedFileObjects,
  deleteReferralDocument = null,
  isReferrer = false,
  currentUser = null,
  isHidePrintAll = false,
  newUploadedFiles = null,
  sendFax = null,
  UploadButtonComponent = null,
}) {

  const [loading, setLoading] = useState(false);
  const referralAnswers = useCallback(
    (question, name) => {
      const ref = data_referral_answers?.answers?.find(
        ref => ref.question.toLowerCase() === question.toLowerCase() || name === ref.name
      );
      return ref?.answer || 'No';
    },
    [data_referral_answers]
  );

  const shouldRenderFile = (uuid, index) => {
    if (!newUploadedFiles) return true;
    if (isEmpty(newUploadedFiles)) return index === 0;
    return newUploadedFiles.includes(uuid);
  };

  const renderFileButtons = (uploadedDoc, key) => {
    const ext = StringUtils.getFileExtension('path', uploadedDoc.url);

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <MyButton
          style={{ justifyContent: 'flex-start' }}
          onClick={() => FileUtils.downloadFile(uploadedDoc)}
          color="primary"
          variant="text"
          startIcon={<IconEye style={{ height: 16, width: 16 }} />}
        >
          {(uploadedDoc.referral_document_name || uploadedDoc.name) + ext}
        </MyButton>

        <Stack direction="row" spacing={1} alignItems="center">
          {deleteReferralDocument && (
            <MyIconButton
              color="error"
              label="Delete Document"
              style={{ fontSize: '14px' }}
              onClick={e => {
                e.stopPropagation();
                deleteReferralDocument(uploadedDoc);
              }}
            >
              <IconX style={{ height: 16, width: 16 }} /> Remove
            </MyIconButton>
          )}

          {/* {sendFax && (
            <MyIconButton
              color="error"
              label="Fax Document"
              style={{ fontSize: '14px' }}
              onClick={e => {
                e.stopPropagation();
                sendFax(uploadedDoc, key);
              }}
            >
              <IconSend style={{ height: 16, width: 16 }} /> Fax
            </MyIconButton>
          )} */}

          {ext === '.pdf' && (
            <>
              <MyIconButton
                color="error"
                label="PDF"
                style={{ fontSize: '14px' }}
                onClick={() => FileUtils.downloadFileAsPDF(uploadedDoc)}
              >
                <IconDownload style={{ height: 16, width: 16 }} /> PDF
              </MyIconButton>
              <MyIconButton
                color="error"
                label="TIF"
                style={{ fontSize: '14px' }}
                onClick={() => FileUtils.exportFileAsPDF(uploadedDoc)}
              >
                <IconDownload style={{ height: 16, width: 16 }} /> TIF
              </MyIconButton>
            </>
          )}

          {(ext === '.jpg' || ext === '.png') && (
            <MyIconButton
              color="error"
              label="TIF"
              style={{ fontSize: '14px' }}
              onClick={() => FileUtils.exportFileAsPDF(uploadedDoc)}
            >
              <IconDownload style={{ height: 16, width: 16 }} /> TIF
            </MyIconButton>
          )}
        </Stack>
      </Stack>
    );

  };

  return (
    <Grid
      container
      spacing={gridSpacing}
      style={label !== 'Referral Documents' ? { marginTop: 16, marginLeft: 16 } : null}
    >
      {loading && <GlobalLoader />}
      <Grid item xs={12}>
        <Stack direction="row">
          <Stack direction="row" spacing={1}>
            <DescriptionIcon />
            <MyTypography variant="h3">{label}</MyTypography>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction={'row'} spacing={2}>
            {UploadButtonComponent}
            {!isHidePrintAll && !isEmpty(uploadedFileObjects) && (
              <>
                <MyButton
                  style={{ width: 100 }}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await FileUtils.printAll(
                        patientDetails,
                        uploadedFileObjects,
                        referralDetails,
                        isReferrer ? currentUser : null
                      );
                    } finally {
                      setLoading(false);
                    }
                  }}
                  color="primary"
                  variant="outlined"
                  disabled={loading}
                >
                  Print All
                </MyButton>

                <MyButton
                  style={{ width: 150 }}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await FileUtils.exportAllFileAsTIF(
                        uploadedFileObjects,
                        patientDetails,
                        referralDetails
                      );
                    } finally {
                      setLoading(false);
                    }
                  }}
                  color="primary"
                  variant="outlined"
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Export All as TIF"}
                </MyButton>
              </>
            )}
          </Stack>
        </Stack>
      </Grid>

      {!isEmpty(uploadedFileObjects) ? (
        Object.entries(uploadedFileObjects).map(([key, docs]) => (
          <Grid item xs={12} key={key}>
            {docs.map((uploadedDoc, index) => {
              if (!shouldRenderFile(uploadedDoc.uuid, index)) return null;

              const hasQuestion =
                uploadedDoc.question &&
                uploadedDoc.question[0]?.question &&
                uploadedDoc.question[0].question !== '?' &&
                data_referral_answers?.answers?.some(
                  ref =>
                    ref.question.toLowerCase() ===
                    uploadedDoc.question[0].question.toLowerCase()
                );

              return (
                <div key={uploadedDoc.uuid}>
                  {index === 0 && (
                    <MyTypography variant="h5" sx={{ mb: 1, mt: -1 }}>
                      {hasQuestion
                        ? `${uploadedDoc.question[0].question}  ${referralAnswers(
                          uploadedDoc.question[0].question,
                          uploadedDoc.name
                        )}`
                        : StringUtils.toTitleCase(uploadedDoc.name)}
                    </MyTypography>
                  )}
                  {renderFileButtons(uploadedDoc, key)}
                </div>
              );
            })}
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <MyTypography variant="h3" align="center">
            No Uploaded Documents.
          </MyTypography>
          <br /><br />
          <MyTypography variant="h5" align="center">
            Please make sure you have provider set to upload documents.
          </MyTypography>
        </Grid>
      )}

      {data_referral_answers?.answers?.map((question, index) => {
        const docs = uploadedFileObjects?.[question.name] || [];
        const hasMatchingQuestion = docs.some(doc =>
          doc.question?.some(q => q.question === question.question)
        );

        if (!hasMatchingQuestion && question.question !== '?') {
          return (
            <Grid item sm={6} xs={12} key={`extra-${index}`}>
              <MyTypography variant="h5">
                {question.question}  {question.answer}
              </MyTypography>
            </Grid>
          );
        }
        return null;
      })}
    </Grid>
  );
}
